// apps/web/src/features/admin/form-config/shared/FormulaEditor.tsx
//
// React wrapper around a CodeMirror 6 editor for formula expressions.
// Provides syntax highlighting, autocomplete, a toolbar, and error display.

'use client';

import { useEffect, useRef, useState, useCallback } from 'react';
import { EditorView, keymap } from '@codemirror/view';
import { EditorState } from '@codemirror/state';
import { defaultKeymap } from '@codemirror/commands';
import {
  formulaHighlighter,
  formulaAutocomplete,
  FORMULA_EDITOR_CSS,
  FUNCTION_CATEGORIES,
} from './formula-lang';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface FormulaEditorProps {
  value: string;
  onChange: (source: string) => void;
  fieldNames: string[];
  functions?: string[];
  scope?: 'record' | 'batch';
  errors?: string[];
  rows?: number;
  readOnly?: boolean;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function FormulaEditor({
  value,
  onChange,
  fieldNames,
  scope,
  errors,
  readOnly = false,
}: FormulaEditorProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const viewRef = useRef<EditorView | null>(null);
  const [showFunctions, setShowFunctions] = useState(false);
  const [showFields, setShowFields] = useState(false);

  // Keep a ref to onChange so the CM listener always calls the latest
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  // Create and destroy the editor
  useEffect(() => {
    if (!containerRef.current) return;

    const extensions = [
      keymap.of(defaultKeymap),
      formulaHighlighter,
      formulaAutocomplete(fieldNames, scope),
      EditorView.updateListener.of((update) => {
        if (update.docChanged) {
          onChangeRef.current(update.state.doc.toString());
        }
      }),
      EditorView.editable.of(!readOnly),
    ];

    const state = EditorState.create({
      doc: value,
      extensions,
    });

    const view = new EditorView({
      state,
      parent: containerRef.current,
    });

    viewRef.current = view;

    return () => {
      view.destroy();
      viewRef.current = null;
    };
    // We intentionally only run on mount/unmount; value sync is handled below
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readOnly]);

  // Sync external value changes into the editor
  useEffect(() => {
    const view = viewRef.current;
    if (!view) return;
    const currentDoc = view.state.doc.toString();
    if (currentDoc !== value) {
      view.dispatch({
        changes: { from: 0, to: currentDoc.length, insert: value },
      });
    }
  }, [value]);

  // Insert text at cursor
  const insertAtCursor = useCallback((text: string) => {
    const view = viewRef.current;
    if (!view) return;
    const pos = view.state.selection.main.head;
    view.dispatch({
      changes: { from: pos, insert: text },
      selection: { anchor: pos + text.length },
    });
    view.focus();
  }, []);

  const handleFunctionClick = useCallback(
    (funcName: string) => {
      insertAtCursor(`${funcName}()`);
      setShowFunctions(false);
    },
    [insertAtCursor],
  );

  const handleFieldClick = useCallback(
    (fieldName: string) => {
      insertAtCursor(`{${fieldName}}`);
      setShowFields(false);
    },
    [insertAtCursor],
  );

  // Filter categories for scope
  const visibleCategories =
    scope === 'record'
      ? Object.fromEntries(
          Object.entries(FUNCTION_CATEGORIES).filter(
            ([key]) => key !== 'Batch Aggregation',
          ),
        )
      : FUNCTION_CATEGORIES;

  return (
    <div className="formula-editor">
      {/* Inject CodeMirror formula styles */}
      <style dangerouslySetInnerHTML={{ __html: FORMULA_EDITOR_CSS }} />

      {/* Toolbar */}
      <div className="formula-editor__toolbar">
        <div style={{ position: 'relative', display: 'inline-block' }}>
          <button
            type="button"
            onClick={() => {
              setShowFunctions((p) => !p);
              setShowFields(false);
            }}
            aria-label="Functions"
          >
            f(x) Functions
          </button>
          {showFunctions && (
            <div
              className="formula-editor__dropdown"
              role="menu"
              style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                zIndex: 10,
                background: '#fff',
                border: '1px solid #d1d5db',
                borderRadius: 4,
                padding: '4px 0',
                minWidth: 180,
                maxHeight: 300,
                overflowY: 'auto',
              }}
            >
              {Object.entries(visibleCategories).map(([category, fns]) => (
                <div key={category}>
                  <div
                    style={{
                      padding: '4px 12px',
                      fontSize: 11,
                      fontWeight: 600,
                      color: '#6b7280',
                      textTransform: 'uppercase',
                    }}
                  >
                    {category}
                  </div>
                  {fns.map((fn) => (
                    <button
                      key={fn}
                      type="button"
                      role="menuitem"
                      onClick={() => handleFunctionClick(fn)}
                      style={{
                        display: 'block',
                        width: '100%',
                        textAlign: 'left',
                        padding: '4px 16px',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        fontSize: 13,
                      }}
                    >
                      {fn}()
                    </button>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>

        <div style={{ position: 'relative', display: 'inline-block' }}>
          <button
            type="button"
            onClick={() => {
              setShowFields((p) => !p);
              setShowFunctions(false);
            }}
            aria-label="Fields"
          >
            {'{ }'} Fields
          </button>
          {showFields && fieldNames.length > 0 && (
            <div
              className="formula-editor__dropdown"
              role="menu"
              style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                zIndex: 10,
                background: '#fff',
                border: '1px solid #d1d5db',
                borderRadius: 4,
                padding: '4px 0',
                minWidth: 160,
                maxHeight: 300,
                overflowY: 'auto',
              }}
            >
              {fieldNames.map((name) => (
                <button
                  key={name}
                  type="button"
                  role="menuitem"
                  onClick={() => handleFieldClick(name)}
                  style={{
                    display: 'block',
                    width: '100%',
                    textAlign: 'left',
                    padding: '4px 16px',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: 13,
                  }}
                >
                  {`{${name}}`}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* CodeMirror container */}
      <div className="formula-editor__cm" ref={containerRef} />

      {/* Error list */}
      {errors && errors.length > 0 && (
        <ul className="formula-editor__errors">
          {errors.map((err, i) => (
            <li key={i} style={{ color: '#dc2626', fontSize: 13 }}>
              {err}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
